/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React from 'react';

/**
 * Medal badge matching reference: scalloped dark blue rosette,
 * thin bright white inner circle outline, dark blue inner circle,
 * white icon (star/checkmark). Two ribbon tails: straight and parallel
 * for most of length, then angle inwards to form V at bottom tips.
 * @param {string} variant - 'ngo' | 'association'
 * @param {string} size - 'small' | 'medium' | 'large'
 */
const MedalBadge = ({ variant = 'association', size = 'medium' }) => {
  const isAssociation = variant === 'association';
  const sizeMap = { small: 40, medium: 56, large: 72 };
  const s = sizeMap[size] || 56;

  const cx = s / 2;
  const cy = s / 2;
  const outerR = s / 2 - 1;
  const innerR = outerR * 0.7;
  const n = 16;
  const points = [];
  for (let i = 0; i < n; i++) {
    const angle = (i * Math.PI * 2) / n - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
  }
  const scallopPath = `M ${points.join(' L ')} Z`;

  const badgeColor = '#1e3a8a';
  const whiteRingR = innerR * 0.9;
  const darkInnerR = whiteRingR * 0.82;

  const ribbonW = s * 0.12;
  const ribbonH = s * 0.28;
  const ribbonTop = cy + whiteRingR;

  // Ribbons: straight and parallel, then angle inwards to V at bottom
  const leftRibbonPath = `M ${cx - ribbonW} ${ribbonTop}
    L ${cx - ribbonW} ${ribbonTop + ribbonH * 0.7}
    L ${cx - ribbonW * 0.5} ${ribbonTop + ribbonH}
    L ${cx} ${ribbonTop + ribbonH * 0.75}
    L ${cx} ${ribbonTop} Z`;

  const rightRibbonPath = `M ${cx} ${ribbonTop}
    L ${cx} ${ribbonTop + ribbonH * 0.75}
    L ${cx + ribbonW * 0.5} ${ribbonTop + ribbonH}
    L ${cx + ribbonW} ${ribbonTop + ribbonH * 0.7}
    L ${cx + ribbonW} ${ribbonTop} Z`;

  const starPath = (() => {
    const r1 = 8;
    const r2 = 3.5;
    const pts = [];
    for (let i = 0; i < 10; i++) {
      const angle = (i * Math.PI) / 5 - Math.PI / 2;
      const r = i % 2 === 0 ? r1 : r2;
      pts.push(`${r * Math.cos(angle)},${r * Math.sin(angle)}`);
    }
    return `M ${pts.join(' L ')} Z`;
  })();

  const checkPath = 'M -6,1 L -2,5 L 6,-5 L 6,-8 L -2,2 L -6,-2 Z';

  return (
    <svg
      width={s}
      height={s + ribbonH}
      viewBox={`0 0 ${s} ${s + ribbonH}`}
      className="medal-badge-svg"
      style={{ display: 'block' }}
    >
      <path d={scallopPath} fill={badgeColor} stroke={badgeColor} strokeWidth="0.5" />
      <circle cx={cx} cy={cy} r={whiteRingR} fill="white" stroke="none" />
      <circle cx={cx} cy={cy} r={darkInnerR} fill={badgeColor} stroke="none" />
      <g transform={`translate(${cx}, ${cy}) scale(${darkInnerR / 10})`} fill="white">
        {isAssociation ? <path d={starPath} /> : <path d={checkPath} />}
      </g>
      <path d={leftRibbonPath} fill={badgeColor} />
      <path d={rightRibbonPath} fill={badgeColor} />
    </svg>
  );
};

export default MedalBadge;
